import React, { useState, useEffect } from "react"
import ReactHtmlParser from "react-html-parser"
import ValueCardsContainer from "../value-cards-container/value-cards-container-component"
import TimelineContainer from "../timeline-container/timeline-container-component"
import ArticleColorUspBlock from "../article-color-usp/article-color-usp-component"
import { generateRandomString } from "../../helpers/keys"
import useWindowWidth from "../../hooks/useWindowWidth"

export default function TextAndTabsContainer({ headline, subheader, tabContent }) {
  //string regex in replace, replaces spaces with dashes.

  const tabLabels = tabContent.map((obj) => obj.tabLabel) || {}

  const convertUtfToReadableString = (url) => {
    return (
      (!!url &&
        url
          .replaceAll("%20", " ")
          .replaceAll("%C3%A6", "æ")
          .replaceAll("%C3%86", "Æ")
          .replaceAll("%C3%B8", "ø")
          .replaceAll("%C3%98", "Ø")
          .replaceAll("%C3%A5", "å")
          .replaceAll("%C3%85", "Å")) ||
      url
    )
  }

  const [urlHash] = useState(convertUtfToReadableString(window.location.href.split("#")[1]) || undefined)
  const [matchingTabLabel] = useState(tabLabels.find((tabLabel) => convertUtfToReadableString(tabLabel) === urlHash) || undefined)
  const [activeTab, setActiveTab] = useState(matchingTabLabel || tabLabels[0] || "")

  useEffect(() => {
    const handleLocationChange = () => {
      // Perform actions when window.location.href changes
      setActiveTab(convertUtfToReadableString(window.location.href.split("#")[1] || "") || tabLabels[0])
    }

    // Add event listener for 'popstate' event
    window.addEventListener("popstate", handleLocationChange)

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("popstate", handleLocationChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array to run the effect only once

  const htmlTabs =
    tabContent.length > 0
      ? tabContent.map((tab, index) => {
          return (
            <Tab
              key={tab.tabLabel ? tab.tabLabel.replace(/\s+/g, "-") : index}
              Id={index}
              tabHelperText={tab.tabHelperText}
              label={tab.tabLabel}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )
        })
      : undefined

  const htmlTabContent =
    tabContent.length > 0
      ? tabContent.map((tab, index) => {
          return (
            <TabContent
              key={tab.tabLabel ? tab.tabLabel.replace(/\s+/g, "-") : index}
              Id={index}
              label={tab.tabLabel}
              activeTab={activeTab}
              textContent={tab.textContent}
              blockContent={tab.blockContent}
            />
          )
        })
      : undefined

  return (
    <section className="text-and-tabs">
      <div className="w--narrow">
        <div className="text-and-tabs-top">
          <h2 data-epi-property="Headline">{headline}</h2>
          <p data-epi-property="Subheader">{subheader}</p>
        </div>
        <div className={`text-and-tabs-tabs`}>{htmlTabs}</div>
        <div data-epi-property="TabContent" className="text-and-tabs-contents">
          {htmlTabContent}
        </div>
      </div>
    </section>
  )
}

function Tab({ label, tabHelperText, activeTab, setActiveTab }) {
  const tabHandler = () => {
    setActiveTab(label)
    const newUrl = window.location.href.split("#")[0] + "#" + label
    window.history.pushState({ urlChange: "TabNavigation" }, "", newUrl)
  }

  return (
    <button
      data-epi-property="TabLabel"
      id={label}
      aria-pressed={label === activeTab ? "true" : "false"}
      aria-label={tabHelperText + " " + label}
      onClick={tabHandler}
      className={label === activeTab ? "is-active" : "inactive"}
    >
      {label}
    </button>
  )
}

function TabContent({ label, activeTab, textContent, blockContent }) {
  //Component was scoped with functionality to include single block item.
  const extractedBlockContent = blockContent ? blockContent : undefined

  useEffect(() => {
    CreateContentBlockElement()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [epiSortedBlockContent, setEpiSortedBlockContent] = useState([])

  const CreateContentBlockElement = () => {
    let htmlBlockElements = []

    if (!!extractedBlockContent && extractedBlockContent.length > 0) {
      extractedBlockContent.forEach((element) => {
        switch (element.__type__) {
          case "ValueCardsContainerBlock":
            htmlBlockElements.push(
              <div key={generateRandomString()} className="text-and-tabs">
                <ValueCardsContainer contentArea={element.contentArea} />
              </div>,
            )
            break
          case "TimelineContainerBlock":
            htmlBlockElements.push(
              <TimelineContainer
                key={generateRandomString()}
                contentArea={element.contentArea}
                isNumbered={element.isNumbered}
                header={element.header}
                description={element.description}
                isVideoFormat={element.isVideoFormat}
              />,
            )
            break
          case "ArticleColorUspBlock":
            htmlBlockElements.push(
              <div key={generateRandomString()} className="text-and-tabs">
                <div className="article-color-usp-module">
                  <ArticleColorUspBlock
                    key={generateRandomString()}
                    titleBoxColor={element.titleBoxColor}
                    titleBoxTextColor={element.titleBoxTextColor}
                    uspBoxColor={element.uspBoxColor}
                    uspBoxTextColor={element.uspBoxTextColor}
                    modifier={"w--wider"}
                    title={element.title}
                    headline={element.headline}
                    uspTexts={element.uspTexts}
                  />
                </div>
              </div>,
            )
            break

          case "PresentationTabItemBlock":
            htmlBlockElements.push(
              <div key={generateRandomString()} className="presentation-tabs">
                <div className="presentation-tabs-contents">
                  <PresentationTabContent desktopImage={element.desktopImage} mobileImage={element.mobileImage} />
                </div>
              </div>,
            )
            break
          default:
            break
        }
      })

      setEpiSortedBlockContent(
        htmlBlockElements.map((element) => {
          return element
        }),
      )
    }
  }

  return (
    <article className={label === activeTab ? "is-active" : "inactive"}>
      <div data-epi-property="TextContent" className="rich-text">
        {ReactHtmlParser(textContent)}
      </div>
      {epiSortedBlockContent}
    </article>
  )
}

function PresentationTabContent({ label, activeTab, desktopImage, mobileImage }) {
  const windowWidth = useWindowWidth()

  return (
    <div className={label === activeTab ? "is-active" : "inactive"}>
      <img data-epi-property="DesktopImage" src={windowWidth >= 768 ? desktopImage : mobileImage}></img>
    </div>
  )
}
